import { Children, isValidElement, type ReactNode, type ComponentPropsWithoutRef } from 'react'
import clsx from 'clsx'
import { InputGroupContext, type GroupPosition } from './InputGroupContext'
import { Button } from '@/common/components/button'
import type { ButtonVariants } from '@/common/components/button/Button.css'
import * as styles from './InputGroup.css'

// =============================================================================
// Types
// =============================================================================

type InputGroupProps = {
  children: ReactNode
  className?: string
}

type InputGroupAddonProps = {
  children: ReactNode
  className?: string
}

type InputGroupButtonProps = {
  children: ReactNode
  className?: string
  /** Whether to show a border that matches input borders. @default true */
  bordered?: boolean
} & ButtonVariants &
  Omit<ComponentPropsWithoutRef<'button'>, 'className'>

// =============================================================================
// Helper
// =============================================================================

function getPosition(index: number, total: number): GroupPosition {
  if (total === 1) return 'standalone'
  if (index === 0) return 'start'
  if (index === total - 1) return 'end'
  return 'middle'
}

// =============================================================================
// Components
// =============================================================================

/**
 * Groups multiple form inputs together with seamless borders.
 *
 * @example
 * ```tsx
 * <InputGroup>
 *   <Input label="Latitude" ... />
 *   <Input label="Longitude" ... />
 *   <InputGroup.Button onClick={handleGeocode}>
 *     <Icon name="my_location" />
 *   </InputGroup.Button>
 * </InputGroup>
 * ```
 */
export function InputGroup({ children, className }: InputGroupProps) {
  const childArray = Children.toArray(children).filter(isValidElement)
  const total = childArray.length

  return (
    <div className={clsx(styles.inputGroup, className)}>
      {childArray.map((child, index) => (
        <InputGroupContext.Provider key={index} value={{ position: getPosition(index, total) }}>
          {child}
        </InputGroupContext.Provider>
      ))}
    </div>
  )
}

/**
 * Static text addon for InputGroup (e.g., "https://", "$")
 */
function InputGroupAddon({ children, className }: InputGroupAddonProps) {
  return (
    <InputGroupContext.Consumer>
      {(context) => (
        <div
          className={clsx(
            styles.addon({ position: context?.position ?? 'standalone' }),
            className
          )}
        >
          {children}
        </div>
      )}
    </InputGroupContext.Consumer>
  )
}

/**
 * Action button for InputGroup (e.g., geocode button)
 * Uses the Button component with position-based border-radius overrides.
 */
function InputGroupButton({
  children,
  className,
  variant = 'muted',
  bordered = true,
  ...buttonProps
}: InputGroupButtonProps) {
  return (
    <InputGroupContext.Consumer>
      {(context) => {
        const position = context?.position ?? 'standalone'
        return (
          <div
            className={clsx(
              styles.buttonWrapper,
              position === 'start' && styles.buttonWrapperStart,
              position === 'middle' && styles.buttonWrapperMiddle
            )}
          >
            <Button
              variant={variant}
              className={clsx(
                styles.buttonStretch,
                position === 'standalone' && styles.buttonPositionStandalone,
                position === 'start' && styles.buttonPositionStart,
                position === 'middle' && styles.buttonPositionMiddle,
                position === 'end' && styles.buttonPositionEnd,
                bordered && styles.buttonWithBorder,
                className
              )}
              {...buttonProps}
            >
              {children}
            </Button>
          </div>
        )
      }}
    </InputGroupContext.Consumer>
  )
}

// Attach sub-components
InputGroup.Addon = InputGroupAddon
InputGroup.Button = InputGroupButton
